import { toCloneSafeEvent } from './clone-safe'
import { subscribeTrpcDebug } from './debug-link'
import {
	isTrpcExtensionMessage,
	TRPC_EXTENSION_PROTOCOL_VERSION,
	TRPC_PAGE_SOURCE,
	type TrpcPageBody,
} from './extension-protocol'

type BridgeWindow = Pick<Window, 'postMessage' | 'addEventListener' | 'removeEventListener'>

/**
 * Lets the tRPC debugger browser extension observe this page's operations.
 * Idle until the extension's content script asks to `connect`: only then is a
 * debug listener subscribed, so pages without the extension pay nothing.
 * Returns an uninstall function.
 */
export function installTrpcExtensionBridge(
	target: BridgeWindow | undefined = typeof window === 'undefined' ? undefined : window,
): () => void {
	// No window (SSR, Node tests): nothing to bridge.
	if (!target) return () => {}
	let stopStreaming: (() => void) | null = null

	const post = (body: TrpcPageBody) =>
		// Posted to this same window, so only its own scripts (and the
		// extension's content script) can receive it.
		target.postMessage(
			{ source: TRPC_PAGE_SOURCE, version: TRPC_EXTENSION_PROTOCOL_VERSION, ...body },
			'*',
		)

	const onMessage = (event: MessageEvent) => {
		if (event.source !== target || !isTrpcExtensionMessage(event.data)) return
		switch (event.data.type) {
			case 'connect':
				stopStreaming ??= subscribeTrpcDebug((debugEvent) =>
					post({ type: 'event', event: toCloneSafeEvent(debugEvent) }),
				)
				break
			case 'disconnect':
				stopStreaming?.()
				stopStreaming = null
				break
		}
	}

	target.addEventListener('message', onMessage)
	post({ type: 'ready' })

	return () => {
		target.removeEventListener('message', onMessage)
		stopStreaming?.()
		stopStreaming = null
	}
}
